import * as React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormGroup,
  FormControlLabel,
  Checkbox,
  CircularProgress,
  Box,
  Typography,
  Alert,
} from "@mui/material";
import toast from "react-hot-toast";
import { useCategories } from "../hooks/useCategories";
import {
  getUserCategoryPermissions,
  setUserCategoryPermissions,
} from "../services/api";

export interface UserCategoriesDialogProps {
  open: boolean;
  onClose: () => void;
  /** Id dell'utente di cui gestire i permessi categoria. */
  userId: string | null;
  /** Etichetta mostrata nel titolo (es. email dell'utente). */
  userLabel?: string;
}

export const UserCategoriesDialog: React.FC<UserCategoriesDialogProps> = ({
  open,
  onClose,
  userId,
  userLabel,
}) => {
  const { data: categories = [], isLoading: categoriesLoading } =
    useCategories();
  const [selected, setSelected] = React.useState<Set<string>>(new Set());
  const [loading, setLoading] = React.useState(false);
  const [saving, setSaving] = React.useState(false);

  React.useEffect(() => {
    if (!open || !userId) return;
    setLoading(true);
    getUserCategoryPermissions(userId)
      .then((names) => setSelected(new Set(names)))
      .catch(() => {
        toast.error("Impossibile caricare i permessi categoria dell'utente");
        setSelected(new Set());
      })
      .finally(() => setLoading(false));
  }, [open, userId]);

  const isUnrestricted = selected.size === 0;

  const toggleCategory = (value: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(value)) next.delete(value);
      else next.add(value);
      return next;
    });
  };

  const handleSave = async () => {
    if (!userId) return;
    setSaving(true);
    try {
      await setUserCategoryPermissions(userId, Array.from(selected));
      toast.success("Permessi categoria aggiornati");
      onClose();
    } catch (err: any) {
      toast.error(
        err?.response?.data?.detail ||
          "Errore durante l'aggiornamento dei permessi categoria"
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        Categorie consentite{userLabel ? ` — ${userLabel}` : ""}
      </DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Seleziona le categorie che questo utente può usare in caricamento
          documenti e Golden Standard. Se non selezioni nessuna categoria,
          l'utente avrà accesso a tutte le categorie (comportamento
          predefinito).
        </Typography>
        {isUnrestricted && !loading && !categoriesLoading && (
          <Alert severity="info" sx={{ mb: 2 }}>
            Nessuna restrizione attiva: l'utente può caricare documenti di
            qualunque categoria.
          </Alert>
        )}
        {loading || categoriesLoading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
            <CircularProgress size={32} />
          </Box>
        ) : (
          <FormGroup>
            {categories.map((cat) => (
              <FormControlLabel
                key={cat.value}
                control={
                  <Checkbox
                    checked={selected.has(cat.value)}
                    onChange={() => toggleCategory(cat.value)}
                  />
                }
                label={cat.label}
              />
            ))}
          </FormGroup>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          Annulla
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          disabled={saving || loading || categoriesLoading}
        >
          {saving ? <CircularProgress size={20} color="inherit" /> : "Salva"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
